import { useMemo } from "react";
import { useApps } from "../shared/api/useApps";
import { T } from "../shared/styles/theme";
import IframeView from "./IframeView";

/**
 * "/" — VQ Home (the nexus-home SWA) in the shell iframe.
 *
 * The home app is the core.portal_apps row with category 'home' (DDL
 * 184_home_portal_app.sql), granted to every signed-in user through the
 * portal-all group. It is found by CATEGORY, not by path, so a path change in
 * the registry can't break the landing page; and category 'home' never shows in
 * the menu (ShellLayout lists only 'dashboard').
 *
 * Never a blank page: while /api/apps/ loads we show "Loading…", and if it fails
 * or returns no home app (e.g. the user isn't resolved as portal-all) we show a
 * plain welcome card instead.
 *
 * The iframe itself, and the token hand-off to it, is IframeView's job.
 */
export default function Home() {
  const { data: apps, loading, error } = useApps();

  const app = useMemo(
    () => (apps || []).find((a) => a.category === "home") || null,
    [apps]
  );

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1, color: T.muted, fontSize: 14 }}>
        Loading...
      </div>
    );
  }

  if (error || !app) {
    return <WelcomeFallback />;
  }

  return <IframeView app={app} />;
}

function WelcomeFallback() {
  return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div
        style={{
          maxWidth: 460,
          textAlign: "center",
          background: T.card,
          border: `1px solid ${T.border}`,
          borderRadius: 12,
          padding: "32px 28px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        }}
      >
        <h1 style={{ fontSize: 20, fontWeight: 600, color: T.primary, margin: "0 0 8px" }}>Welcome to Nexus</h1>
        <p style={{ fontSize: 13, color: T.muted, margin: 0, lineHeight: 1.6 }}>
          The VQ Home page isn't available right now. Any dashboards you have access to are in
          the menu above. If this persists, please contact IT.
        </p>
      </div>
    </div>
  );
}
